import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  COMPLAINT_REASON_LABELS,
  COMPLAINT_STATUS_LABELS,
  type ComplaintReason,
  ComplaintStatus,
} from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Reclamo {
  id: string;
  reason: ComplaintReason;
  detail: string | null;
  status: ComplaintStatus;
  resolution: string | null;
  createdAt: string;
}

/**
 * Reportar un problema con el pedido.
 *
 * Motivo cerrado más detalle libre. El motivo permite contar —«el 40 % de los
 * reclamos de este comercio son por producto faltante»— y el detalle es lo que
 * de verdad explica el caso concreto.
 */
export function Reclamar({ orderId }: { orderId: string }) {
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState<ComplaintReason | null>(null);
  const [detalle, setDetalle] = useState('');

  const reclamos = useQuery({
    queryKey: ['reclamos', orderId],
    queryFn: async () => (await api.get<Reclamo[]>(`/pedidos/${orderId}/reclamos`)).data,
  });

  const enviar = useMutation({
    mutationFn: async () =>
      (
        await api.post<Reclamo>(`/pedidos/${orderId}/reclamos`, {
          reason: motivo,
          ...(detalle.trim() ? { detail: detalle.trim() } : {}),
        })
      ).data,
    onSuccess: () => {
      avisos.exito('Reclamo enviado. Te respondemos por aquí y por correo.');
      setAbierto(false);
      setMotivo(null);
      setDetalle('');
      void cliente.invalidateQueries({ queryKey: ['reclamos', orderId] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  const existentes = reclamos.data ?? [];

  return (
    <section className="card-kick mt-4 p-5">
      <h2 className="text-lg text-kick-dark">¿Algo salió mal?</h2>

      {existentes.length > 0 && (
        <div className="mt-3 space-y-2">
          {existentes.map((r) => (
            <div key={r.id} className="rounded-kick border border-kick-gray/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-kick-ink">
                  {COMPLAINT_REASON_LABELS[r.reason]}
                </p>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-xs font-bold',
                    r.status === ComplaintStatus.RESOLVED
                      ? 'bg-emerald-100 text-emerald-800'
                      : r.status === ComplaintStatus.REJECTED
                        ? 'bg-kick-gray/15 text-kick-gray'
                        : 'bg-amber-100 text-amber-800',
                  )}
                >
                  {COMPLAINT_STATUS_LABELS[r.status]}
                </span>
              </div>

              {r.detail && <p className="mt-1 text-sm text-kick-gray">{r.detail}</p>}

              {r.resolution && (
                <p className="mt-2 rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
                  <span className="font-semibold">Respuesta: </span>
                  {r.resolution}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {!abierto ? (
        <div className="mt-3">
          <Boton
            variante="secundario"
            icono={<AlertTriangle className="h-4 w-4" />}
            onClick={() => setAbierto(true)}
          >
            {existentes.length > 0 ? 'Reportar otro problema' : 'Reportar un problema'}
          </Boton>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-kick-ink">¿Qué pasó?</p>
            <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Motivo">
              {Object.entries(COMPLAINT_REASON_LABELS).map(([valor, texto]) => (
                <button
                  key={valor}
                  type="button"
                  role="radio"
                  aria-checked={motivo === valor}
                  onClick={() => setMotivo(valor as ComplaintReason)}
                  className={cn(
                    'rounded-kick border px-3 py-2 text-sm font-semibold transition',
                    motivo === valor
                      ? 'border-kick-red bg-kick-red text-white'
                      : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
                  )}
                >
                  {texto}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="detalle" className="text-sm font-semibold text-kick-ink">
              Cuéntanos qué pasó
            </label>
            <textarea
              id="detalle"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Faltó la gaseosa de 1.5 litros que venía con el combo…"
              className="mt-1.5 w-full rounded-kick border border-kick-gray/30 px-3 py-2 text-sm text-kick-ink outline-none transition focus:border-kick-red"
            />
            <p className="mt-1 text-xs text-kick-gray">
              Entre más concreto, más rápido lo resolvemos con el comercio.
            </p>
          </div>

          <div className="flex gap-2">
            <Boton onClick={() => enviar.mutate()} cargando={enviar.isPending} disabled={!motivo}>
              Enviar reclamo
            </Boton>
            <Boton variante="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
        </div>
      )}
    </section>
  );
}
